import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { KubaExportService } from './kuba-export.service';
import { FeatureKey } from 'src/app/_models';
import { FeaturesBaseServices } from 'src/app/kuba/features/services/features-base.services';
import { FeatureBase } from 'src/app/kuba/features/models/features-base.model';
import { BusinessServices } from 'src/app/kuba/businesses/services/business.services';
import { BaseServices } from './../../kuba.services';
import { Subscription } from 'rxjs';
import { BusinessExportFilterRequest } from '../../businesses/models/businessFilterRequest';
@Component({
  selector: 'kuba-export',
  templateUrl: 'kuba-export.component.html',
  standalone: false
})

export class KubaExportComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  @Input() applicationId: number;
  @Input() isPortal: boolean = false;
  @Input() portalId: number = 0;
  @Input() reportData: any;
  @Input() additionalData: any;
  @Input() printMode?: number;
  @Input() isPDF?: boolean;
  @Input() filters?: any;
  @Input() exportAllData?: boolean = false;
  @Input() getAllDataFn?: () => Promise<any[]>;
  @Output() loading = new EventEmitter();
  exportFilename: string;
  showFormat = true;
  isPdfonly = false;
  featureBase: FeatureBase;
  private subscriptions: Subscription[] = [];

  constructor(private kubaExportService: KubaExportService,
              private translate: TranslateService, public featuresBaseServices: FeaturesBaseServices) { }

  ngOnInit() {
    this.isPdfonly = this.isPDF != null ? true : false;
  }

  exportReport(type: string) {
    this.loading.emit(true);

    let businessId = 0;
    let CultureInfo = "en-GB";
    if (this.additionalData.businessId != undefined) {
      businessId = this.additionalData.businessId;
    }
    if (this.additionalData.cultureInfo != undefined) {
      CultureInfo = this.additionalData.cultureInfo;
    }

    let languageMode = sessionStorage.getItem('languageMode');
    if (languageMode != null) {
      CultureInfo = languageMode;
      this.exportFilename = this.translate.instant(this.additionalData.fileName);
    }

    if (this.getAllDataFn) {
      this.getAllDataFn().then(allData => {
        this.handleExportWithData(type, businessId, CultureInfo, allData);
      }).catch(() => {
        this.handleExportError();
      });
      return;
    }

    this.handleExport(type, businessId, CultureInfo);
  }

  private handleExport(type: string, businessId: number, CultureInfo: any): void {
    const exportParams = this.buildExportParams(type, businessId, CultureInfo);
    const exportObservable = this.getExportObservable(exportParams);

    this.subscriptions.push(
      exportObservable.subscribe({
        next:blob => this.handleExportSuccess(blob, type),
        error: () => this.handleExportError()
      })
    );
  }

  private handleExportWithData(type: string, businessId: number, CultureInfo: any, allData: any[]): void {
    const exportParams = this.buildExportParams(type, businessId, CultureInfo);
    const exportObservable = this.kubaExportService.generateExportFile(
      allData,
      exportParams.columnNames,
      exportParams.type,
      exportParams.header,
      exportParams.printMode,
      exportParams.businessId,
      exportParams.CultureInfo
    );
    this.subscriptions.push(
      exportObservable.subscribe({
        next:blob => this.handleExportSuccess(blob, type),
        error: () => this.handleExportError()
      })
    );
  }

  private buildExportParams(type: string, businessId: number, CultureInfo: any) {
    return {
      type,
      businessId,
      CultureInfo,
      columnNames: this.additionalData.columnNames,
      header: this.additionalData.header,
      printMode: this.printMode ?? 0 // Use 0 as default if printMode is undefined
    };
  }

  private getExportObservable(params: any) {
    if (this.shouldExportAllDataWithFilters()) {
      // Check if it's business filters
      if (this.isBusinessFilter()) {
        return this.isPortal && this.portalId > 0
          ? this.kubaExportService.generateExportFileWithAllBusinessesAndFilters(
            this.filters,
            this.applicationId,
            params.columnNames,
            params.type,
            params.header,
            params.printMode,
            this.portalId,
            params.CultureInfo
          )
          : this.kubaExportService.generateExportFileWithAllBusinessesAndFilters(
            this.filters,
            this.applicationId,
            params.columnNames,
            params.type,
            params.header,
            params.printMode,
            null,
            params.CultureInfo
          );
      }
      
      // Default to deviation filters
      return this.isPortal && this.portalId > 0
        ? this.kubaExportService.generateExportFileWithAllDataAndFilters(
          this.filters,
          this.applicationId,
          params.columnNames,
          params.type,
          params.header,
          params.printMode,
          params.businessId,
          this.portalId,
          params.CultureInfo
        )
        : this.kubaExportService.generateExportFileWithAllDataAndFilters(
          this.filters,
          this.applicationId,
          params.columnNames,
          params.type,
          params.header,
          params.printMode,
          params.businessId,
          null,
          params.CultureInfo
        );
    }

    return this.kubaExportService.generateExportFile(
      this.reportData,
      params.columnNames,
      params.type,
      params.header,
      params.printMode,
      params.businessId,
      params.CultureInfo
    );
  }

  private shouldExportAllDataWithFilters(): boolean {
    return ( this.exportAllData &&Boolean(this.filters) && this.applicationId > 0
    );
  }

  private handleExportSuccess(blob: Blob, type: string): void {
    this.loading.emit(false);
    this.downloadFile(blob, type);
  }

  private handleExportError(): void {
    this.loading.emit(false);
    this.showExportErrorMessage();
  }

  private showExportErrorMessage(): void {
    this.notifications.error('EXPORT_NOT_DOWNLOAD');
  }

  private downloadFile(blob: Blob, type: string) {
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${this.exportFilename}.${type.toLowerCase()}`;
    link.click();
  }

  private isBusinessFilter(): boolean {
    // Check if filters object has business-specific properties
    return ( this.filters && (
      'CompanyName' in this.filters ||
      'ConnectedTo' in this.filters ||
      'CategoryName' in this.filters ||
      'Status' in this.filters ||
      'LicenseMonth' in this.filters ||
    
      // as they are optional in BusinessExportFilterRequest
      (this.filters as any).hasOwnProperty('CompanyName') ||
      (this.filters as any).hasOwnProperty('ConnectedTo') ||
      (this.filters as any).hasOwnProperty('CategoryName') ||
      (this.filters as any).hasOwnProperty('Status') ||
      (this.filters as any).hasOwnProperty('LicenseMonth'))
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
